import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MapPin, Calendar, Camera, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PhotographerView({ user }) {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRoster();
  }, []);

  const fetchRoster = async () => {
    try {
      const response = await axios.get(`${API}/photographer/roster`);
      setRoster(response.data.assignments || []);
    } catch (error) {
      toast.error('Failed to fetch roster');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (planId) => {
    try {
      await axios.post(`${API}/photographer/acknowledge/${planId}`);
      toast.success('Plan acknowledged!');
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  return (
    <div className="flex">
      <Sidebar user={user} />
      <div className="main-content flex-1">
        <div className="page-header">
          <h1 className="page-title">My Roster</h1>
          <p className="page-subtitle">Your upcoming photography assignments</p>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        ) : roster.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No assignments yet</h3>
            <p className="text-slate-600">You don't have any upcoming assignments</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {roster.map((assignment) => (
              <Card key={assignment.id} className="glass-card p-6" data-testid={`roster-item-${assignment.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex items-center justify-center">
                        <Camera className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">
                          {assignment.location?.name || 'Location'}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {assignment.time_slot?.start_time} - {assignment.time_slot?.end_time}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-600 mb-1">Coverage Role</p>
                        <p className="font-medium">{assignment.coverage_role}</p>
                      </div>
                      <div>
                        <p className="text-slate-600 mb-1">Address</p>
                        <p className="font-medium">{assignment.location?.address}</p>
                      </div>
                    </div>

                    {assignment.task && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-900"><strong>Task:</strong> {assignment.task}</p>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => handleAcknowledge(assignment.plan_id)}
                    className="btn-gradient"
                    data-testid={`acknowledge-btn-${assignment.id}`}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Acknowledge
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
